/**
 * Extract FAQ-style Q&A pairs from an article's HTML body.
 *
 * Heuristic: any <h2>/<h3>/<h4> whose text ends with "?" is treated as a
 * question; the answer is the concatenated text of the following sibling
 * <p>/<ul>/<ol> elements up to the next heading.
 *
 * Returns an empty array when fewer than 2 pairs are found — so we only
 * emit FAQPage schema when there's a real Q&A structure.
 */
export interface FaqPair {
  question: string;
  answer: string;
}

function stripTags(s: string): string {
  return s
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

export function extractFaqPairs(html: string): FaqPair[] {
  if (!html) return [];
  // Match heading + everything up to the next heading-or-end.
  const re =
    /<(h[2-4])[^>]*>([\s\S]*?)<\/\1>\s*([\s\S]*?)(?=<h[2-4][^>]*>|$)/gi;
  const pairs: FaqPair[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const qRaw = stripTags(m[2] ?? "");
    if (!qRaw.endsWith("?") || qRaw.length < 6 || qRaw.length > 220) continue;

    const block = m[3] ?? "";
    // Pull text out of paragraphs/lists; ignore figures, scripts, etc.
    const textParts: string[] = [];
    const partRe = /<(p|ul|ol|blockquote)[^>]*>([\s\S]*?)<\/\1>/gi;
    let pm: RegExpExecArray | null;
    while ((pm = partRe.exec(block)) !== null) {
      const t = stripTags(pm[2] ?? "");
      if (t) textParts.push(t);
      if (textParts.join(" ").length > 600) break;
    }
    const answer = textParts.join(" ").slice(0, 600).trim();
    if (answer.length < 40) continue;
    pairs.push({ question: qRaw, answer });
  }
  // Dedupe by question.
  const seen = new Set<string>();
  const unique = pairs.filter((p) => {
    const k = p.question.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return unique.length >= 2 ? unique.slice(0, 12) : [];
}

/** Plain-text length of an HTML body — used to gate "long post" behavior. */
export function plainTextLength(html: string): number {
  return stripTags(html ?? "").length;
}
